import { containsAnyEntityId } from '@pkg/api/query/queryInvalidation'
import { getTableById } from '@pkg/db/utils'
import { logger } from '@pkg/shared/logger'
import type { QueryClient } from '@tanstack/react-query'
import { tableHashesFor as appTableHashesFor } from './queryIndex'

export interface CacheInvalidationEvent {
  rows: string[]
  referencedIds: string[]
}

export interface InvalidateOptions {
  /**
   * Override for the table → queryHash lookup. Defaults to the app-level
   * index started by `startQueryIndex()`. Tests pass their own.
   */
  tableHashesFor?: (tables: Iterable<string>) => Set<string>
}

/**
 * Hybrid id-scan + table-scan invalidation pass.
 *
 * - Own row ids feed both the table-scan (catches inserts and list/count
 *   queries that have no cached row yet) and the id-scan (catches queries
 *   that already cached the row).
 * - Referenced ids feed only the id-scan: table-scanning every reference's
 *   table would invalidate every patient-touching query whenever any
 *   pharmacy changes — far too broad.
 *
 * Failure mode: any error inside the hook (regex throw, walker overrun,
 * oversized event payload) falls back to invalidating the entire cache.
 * Correct, slow, recoverable.
 */
export function invalidateForRowsChanged(client: QueryClient, e: CacheInvalidationEvent, options?: InvalidateOptions) {
  try {
    if (!e.rows.length && !e.referencedIds.length) return

    const ownTables = new Set<string>()
    for (const id of e.rows) ownTables.add(getTableById(id as never))

    const idsSet = new Set<string>([...e.rows, ...e.referencedIds])

    const lookup = options?.tableHashesFor ?? appTableHashesFor
    const matched = lookup(ownTables)

    const queryCache = client.getQueryCache()
    for (const q of queryCache.getAll()) {
      if (matched.has(q.queryHash)) continue
      if (containsAnyEntityId(q.queryKey, idsSet) || containsAnyEntityId(q.state.data, idsSet)) {
        matched.add(q.queryHash)
      }
    }

    if (matched.size === 0) return

    void client.invalidateQueries({
      predicate: q => matched.has(q.queryHash),
    })
  } catch (err) {
    logger.error({ err }, 'cache invalidation hook failed; falling back to invalidate-all')
    void client.invalidateQueries()
  }
}
